// Generated by tutorials/record.cjs from tutorials/articles/h19-glossary.cjs.
// Do not edit: change the script and run `node tutorials/record.cjs h19 --text-only` (wording) or
// `node tutorials/record.cjs h19` (pictures and clips).
import type { HelpArticle } from '../types';

const article: HelpArticle = {
  slug: 'glossary',
  catalogId: 'h19',
  title: 'Words you\'ll see in Dietech',
  summary: 'Plain meanings for the words Dietech\'s screens and help articles use: diet type, consistency, meal pattern, adjustment, menu cycle, tray line, AIM, EHR mapping and more, each with the article that covers it.',
  audience: 'Everyone who uses Dietech Online, especially if you are new',
  area: 'general',
  learn: [
    'The words for a resident\'s diet: diet type, consistency, Diet Fluctuations',
    'Meal patterns, adjustments and Add To Menu',
    'Menu cycles, menu categories and Auto Item Mods',
    'The tray line, tray tickets and Selective Touch',
    'The Print Queue, EHR mapping and locked diet orders',
  ],
  keywords: [
    'glossary',
    'words',
    'terms',
    'definitions',
    'meaning',
    'what is',
    'diet type',
    'consistency',
    'texture',
    'liquids',
    'diet fluctuations',
    'meal pattern',
    'global',
    'adjustment',
    'never serve',
    'add to menu',
    'standard',
    'in mc',
    'menu cycle',
    'menu category',
    'tray line',
    'set',
    'tray',
    'tray ticket',
    'selective touch',
    'selective menu',
    'aim',
    'auto item mods',
    'significant weight change',
    'ehr mapping',
    'print queue',
  ],
  steps: [
    {
      id: 'diet-type',
      part: 'Residents and diets',
      heading: 'Diet type',
      text: 'What the resident may eat for their health, such as Regular, Cardiac or Renal. A resident can have up to five diet types at once. Changing a diet order covers it.',
      media: {
        image: {
          src: '/help/glossary/diet-type.webp',
          width: 1280,
          height: 334,
        },
        alt: 'A resident\'s Diet Order section, with Diet Type 1, Regular, marked.',
      },
    },
    {
      id: 'consistency',
      part: 'Residents and diets',
      heading: 'Consistency',
      text: 'How the food and drinks are made: the food texture, such as Mechanical Soft or Pureed, and the liquid consistency, such as Thin Liquids or Mildly Thick Liquids.\n\nPut the texture in Consistency 1 and the liquids in Consistency 2, the same way for every resident, so tickets and reports always read the same. Changing a diet order covers it.',
      media: {
        image: {
          src: '/help/glossary/consistency.webp',
          width: 1280,
          height: 334,
        },
        alt: 'The same Diet Order section with the Consistencies heading marked, above Consistency 1, Regular, and Consistency 2, Thin Liquids.',
      },
    },
    {
      id: 'in-mc',
      part: 'Residents and diets',
      heading: 'In MC',
      text: 'Short for "in the menu cycle". In the lists of diet types and consistencies, a check under In MC means your menu cycle has menus written for it. One with no check has nothing on the menu yet, so tell whoever plans your menus. Admitting a new resident covers it.',
      textOnly: true,
    },
    {
      id: 'diet-fluctuations',
      part: 'Residents and diets',
      heading: 'Diet Fluctuations',
      text: 'The history of a resident\'s diet orders, newest first, each with the date it started. The newest one that has started is the diet in use. An order can also start later or end on a date. Changing a diet order covers it.',
      media: {
        image: {
          src: '/help/glossary/diet-fluctuations.webp',
          width: 1280,
          height: 334,
        },
        alt: 'Diet Fluctuations for Navarro, Dolores: her current order with minced and moist food and mildly thick liquids at the top, and her admission order below.',
      },
    },
    {
      id: 'meal-pattern',
      part: 'Residents and diets',
      heading: 'Meal pattern',
      text: 'What a resident gets at each meal on top of, or instead of, the menu, such as coffee at breakfast or a snack at bedtime. Some facilities call it a standing order. Meal patterns covers it.',
      media: {
        image: {
          src: '/help/glossary/meal-pattern.webp',
          width: 1280,
          height: 442,
        },
        alt: 'A resident\'s Meal Pattern: Coffee at breakfast, Iced Tea at lunch and Graham Crackers at the HS snack.',
      },
    },
    {
      id: 'global-meal-pattern',
      part: 'Residents and diets',
      heading: 'Global meal pattern',
      text: 'A meal pattern item your facility keeps in one place and gives to many residents, such as water with meals. On a resident it shows the globe icon, and a change to the global item reaches everyone who has it. Meal patterns covers it.',
      textOnly: true,
    },
    {
      id: 'add-to-menu',
      part: 'Residents and diets',
      heading: 'Add To Menu and Standard',
      text: 'How a meal pattern item reaches the tray. Standard takes the place of the menu\'s item in the same menu category. Add To Menu gives it as well as the menu\'s item. Add When No Similar Item Found adds it only when the menu has nothing like it. Meal patterns covers it.',
      textOnly: true,
    },
    {
      id: 'adjustment',
      part: 'Residents and diets',
      heading: 'Adjustment',
      text: 'A rule that changes what one resident gets from the menu. The most common action is Never Serve, for a dislike or an allergy, and Replace With serves another item instead. Other actions, such as Next Available, Modify, Diet Override and Allow, are offered by the Type you pick. Adjustments covers it.',
      textOnly: true,
    },
    {
      id: 'significant-weight-change',
      part: 'Residents and diets',
      heading: 'Significant weight change',
      text: 'A weight gain or loss bigger than your facility\'s limit over 1, 3 or 6 months. Weight Entry turns the cell yellow, and the dashboard lists the resident under Weight Alerts. Entering weights covers it.',
      media: {
        image: {
          src: '/help/glossary/significant-weight-change.webp',
          width: 818,
          height: 278,
        },
        alt: 'The Weight Alerts card on the dashboard, with two residents marked Significant Change and one Overdue.',
      },
    },
    {
      id: 'menu-cycle',
      part: 'Menus',
      heading: 'Menu cycle',
      text: 'Your facility\'s menu: a set number of days of meals that repeats, such as a 28-day fall and winter cycle. One cycle is active at a time. Your menu cycles covers it.',
      textOnly: true,
    },
    {
      id: 'menu-category',
      part: 'Menus',
      heading: 'Menu category',
      text: 'A part of the meal on the menu, such as SOUP, ENTREE or DESSERT. A meal pattern item set to Standard takes the place of the menu\'s item in its menu category. Reading a menu cycle covers it.',
      textOnly: true,
    },
    {
      id: 'auto-item-mods',
      part: 'Menus',
      heading: 'AIM (Auto Item Mods)',
      text: 'Rules that change a menu item for a diet order without anyone doing it by hand: baked ham becomes roast turkey for a cardiac diet, or the same item is served pureed for a pureed texture. Auto Item Mods (AIM) covers it.',
      textOnly: true,
    },
    {
      id: 'tray-line',
      part: 'Kitchen and tray line',
      heading: 'Tray line, set and tray',
      text: 'The tray line is the order trays are made and delivered in. A set is a cart or a group of trays, and the tray number is a room\'s place in its set: tray 1 is made first. Setting up the tray line covers it.',
      textOnly: true,
    },
    {
      id: 'tray-ticket',
      part: 'Kitchen and tray line',
      heading: 'Tray ticket',
      text: 'The slip that goes on each resident\'s tray, with their name, room, diet order, allergies and the items for that meal. Tickets print in tray line order. Previewing and printing tray tickets covers it.',
      textOnly: true,
    },
    {
      id: 'selective-touch',
      part: 'Kitchen and tray line',
      heading: 'Selective Touch and selective menu',
      text: 'A selective menu is a meal where the resident chooses what they want. Selective Touch is the screen for taking those choices on a tablet or touch screen, from a menu that already fits each resident\'s diet. Taking menu orders with Selective Touch covers it.',
      textOnly: true,
    },
    {
      id: 'print-queue',
      part: 'Words for reports',
      heading: 'Print Queue',
      text: 'The list of reports you made, newest first, with where each one stands: still being made, ready to open, or printed. Open or print one again from there. Favorites and the print queue covers it.',
      textOnly: true,
    },
    {
      id: 'ehr-mapping',
      part: 'Words for your EHR',
      heading: 'EHR mapping',
      text: 'Telling Dietech what a phrase from your EHR means, such as "Mech soft" means the consistency Mechanical Soft. You map each phrase once, and every order with it is handled from then on. Map EHR text to Dietech covers it.',
      textOnly: true,
    },
    {
      id: 'locked-diet-order',
      part: 'Words for your EHR',
      heading: 'Locked diet order',
      text: 'A diet order your staff locked so the next order from your EHR does not replace it. Orders your EHR sends while it is locked wait under Held EHR changes for someone to review. Only some facilities use locking. How your EHR connection works covers it.',
      textOnly: true,
    },
  ],
  tips: [
    'Looking for one word? Press Ctrl and F in your browser and type it.',
    'Your facility may use its own word for some things, such as Patient for Resident or Standing Order for Meal Pattern. The screens use your facility\'s words.',
  ],
  notes: [],
  related: [
    'change-diet-order',
    'meal-patterns',
    'allergies-likes-dislikes',
    'entering-weights',
    'menu-cycles',
    'auto-item-mods',
    'tray-line-setup',
    'tray-tickets',
    'selective-touch',
    'favorites-and-print-queue',
    'map-ehr-text',
    'ehr-connection',
    'adjustments',
    'printers-and-printing',
  ],
  recorded: '2026-10-28',
};

export default article;
